import type { ReactNode } from "react";
import {
  Box,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconShieldLock } from "@tabler/icons-react";
import { ColorSchemeToggle } from "../ColorSchemeToggle";
import { NextGenBrand } from "../NextGenBrand";
import styles from "./WorkspaceAuthShell.module.css";

export interface WorkspaceAuthShellProps {
  workspaceLabel: string;
  eyebrow?: string;
  description?: string;
  accent?: string;
  icon?: ReactNode;
  panelEyebrow?: string;
  panelTitle?: string;
  panelDescription?: string;
  highlights?: string[];
  children: ReactNode;
}

export function WorkspaceAuthShell({
  workspaceLabel,
  eyebrow = "NextGen Diagnostics · Secure clinical access",
  description = "Move from request to verified result in one protected clinical workspace.",
  accent = "var(--evolys-brand-primary)",
  icon,
  panelEyebrow = "STAFF ACCESS · 01",
  panelTitle = "Sign in",
  panelDescription,
  highlights = [
    "Role-scoped workspaces",
    "Protected staff sessions",
    "Audited clinical access",
  ],
  children,
}: WorkspaceAuthShellProps) {
  return (
    <Box
      className={styles.page}
      style={{ "--auth-accent": accent }}
    >
      <Box component="header" className={styles.topbar}>
        <Group h="100%" px={{ base: "md", sm: "xl" }} justify="space-between">
          <Group gap="sm">
            <NextGenBrand
              imageSize={42}
              subtitle="Secure diagnostics powered by EvoLys"
            />
          </Group>
          <ColorSchemeToggle />
        </Group>
      </Box>

      <Box component="main" className={styles.content}>
        <Box component="section" className={styles.story}>
          <Stack className={styles.storyCopy} gap="xl">
            <Text className={styles.eyebrow}>{eyebrow}</Text>
            <Title className={styles.title}>{workspaceLabel}</Title>
            <Text size="lg" c="dimmed" maw={610} lh={1.65}>
              {description}
            </Text>
            <Group className={styles.index} gap="xl">
              {highlights.map((item) => (
                <Box className={styles.indexItem} key={item}>
                  <span className={styles.indexMarker} aria-hidden />
                  <Text size="xs" fw={650}>
                    {item}
                  </Text>
                </Box>
              ))}
            </Group>
          </Stack>
        </Box>

        <Paper className={styles.panel} p="xl" radius="xl">
          <Stack gap="xl">
            <Group justify="space-between" align="flex-start">
              <Stack gap={4}>
                <Text className={styles.panelCode}>{panelEyebrow}</Text>
                <Title order={2}>{panelTitle}</Title>
                <Text size="sm" c="dimmed">
                  {panelDescription ?? `Continue to ${workspaceLabel}.`}
                </Text>
              </Stack>
              <ThemeIcon
                size={44}
                radius="lg"
                variant="light"
                style={{
                  color: accent,
                  background: `color-mix(in srgb, ${accent} 11%, transparent)`,
                }}
              >
                {icon ?? <IconShieldLock size={23} stroke={1.6} />}
              </ThemeIcon>
            </Group>
            {children}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
